/*!
 * lazy-init.js — prepara automáticamente todas las imágenes para vanilla-lazyload
 * -----------------------------------------------------------------------------
 * Qué hace:
 *   1. Convierte  src/srcset  ->  data-src/data-srcset  y añade la clase .lazy
 *   2. Reconstruye el srcset usando el CDN de Shopify (?width=) para que mobile
 *      descargue una variante pequeña, sin tocar los archivos originales
 *   3. Deja las primeras imágenes (hero / above the fold) en carga normal, con
 *      fetchpriority alto, para no perjudicar el LCP
 *   4. Observa el DOM: cubre imágenes que aparecen después (quick view, carrito,
 *      scroll infinito, secciones cargadas por AJAX)
 *
 * Orden de carga en theme.liquid, dentro de <head>:
 *   <script src="{{ 'lazyload.min.js' | asset_url }}"></script>
 *   <script src="{{ 'lazy-init.js'   | asset_url }}"></script>
 * Sin defer y en <head>: así el observer se instala antes de que el navegador
 * parsee el <body> y alcanza a interceptar las imágenes.
 */
(function () {
  'use strict';

  var CONFIG = {
    // Cuántas imágenes SIN atributo loading declarado se dejan en carga
    // normal. Es solo un fallback: si el markup ya dice loading="lazy" o
    // loading="eager"/fetchpriority, ese valor manda y este contador no
    // interviene. Súbelo solo si tu above-the-fold tiene imágenes sin declarar.
    eagerCount: 2,

    // Nunca lazy-loadear lo que matchee esto
    skipSelector: [
      '.no-lazy', '[data-no-lazy]',
      'header img', '.header img', '.site-header img',
      '.announcement-bar img',
      '.swiper-slide-active img'
    ].join(', '),

    // Reconstruir srcset con el CDN de Shopify (pon false en otras plataformas)
    shopifyResponsive: true,

    // Anchos que se piden al CDN
    widths: [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048],

    // Fallback de sizes cuando la imagen no lo trae. AJÚSTALO a tu layout:
    // este asume ancho completo en mobile y media pantalla en desktop.
    defaultSizes: '(max-width: 749px) 100vw, 50vw',

    // Píxeles de anticipación antes de entrar al viewport
    threshold: 300,

    // true = delega en loading="lazy" nativo (menos JS, menos control).
    // false = IntersectionObserver de la librería (recomendado si quieres
    // callbacks, placeholders o soporte de background-images).
    useNative: false,

    // Procesar también elementos con background-image en el atributo style
    handleInlineBackgrounds: true
  };

  var CDN_RE = /(cdn\.shopify\.com|\/cdn\/shop\/)/;
  var eagerBudget = CONFIG.eagerCount;

  /* ---------------------------------------------------------------- helpers */

  function isShopifyImage(url) {
    return !!url && CDN_RE.test(url);
  }

  function stripParams(url) {
    return url.split('?')[0];
  }

  function buildSrcset(url) {
    var base = stripParams(url);
    return CONFIG.widths.map(function (w) {
      return base + '?width=' + w + ' ' + w + 'w';
    }).join(', ');
  }

  function alreadyHandled(el) {
    return el.hasAttribute('data-src') ||
           el.hasAttribute('data-srcset') ||
           el.hasAttribute('data-bg') ||
           el.classList.contains('lazy') ||
           el.dataset.lazyInit === '1';
  }

  function shouldSkip(img) {
    var src = img.getAttribute('src') || '';
    if (!src && !img.getAttribute('srcset')) return true;   // sin fuente útil
    if (src.indexOf('data:') === 0) return true;            // inline / SVG base64
    if (img.closest && img.closest('noscript')) return true;
    try { if (img.matches(CONFIG.skipSelector)) return true; } catch (e) {}
    return false;
  }

  /* --------------------------------------------------------------- <img> */

  function prepareImg(img) {
    if (alreadyHandled(img) || shouldSkip(img)) return;
    img.dataset.lazyInit = '1';

    var src = img.getAttribute('src') || '';
    var srcset = img.getAttribute('srcset') || '';
    var declared = img.getAttribute('loading');
    var prioritized = img.hasAttribute('fetchpriority');

    // 1) Responsive: pedirle al CDN la variante correcta
    if (CONFIG.shopifyResponsive && isShopifyImage(src) && !srcset) {
      srcset = buildSrcset(src);
      if (!img.getAttribute('sizes')) img.setAttribute('sizes', CONFIG.defaultSizes);
    }

    // 2) El markup manda.
    //    Si el tema marcó la imagen como prioritaria o de carga inmediata, no
    //    la diferimos ni le tocamos la prioridad: normalmente es el elemento
    //    LCP, y competir con él empeora la métrica. Solo le mejoramos el srcset.
    if (prioritized || declared === 'eager') {
      if (srcset) img.setAttribute('srcset', srcset);
      return;
    }

    // 3) Si el markup NO declara nada, aplicamos la heurística posicional:
    //    las primeras imágenes sin declarar se dejan en carga normal.
    //    Nunca asignamos fetchpriority por nuestra cuenta.
    if (!declared && eagerBudget > 0) {
      eagerBudget--;
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('loading', 'eager');
      return;
    }

    // 4) El resto (incluido todo lo que el tema marcó loading="lazy") pasa a lazy
    img.setAttribute('data-src', src);
    if (srcset) img.setAttribute('data-srcset', srcset);
    img.removeAttribute('srcset');

    // Placeholder transparente del mismo aspecto: evita el "salto" (CLS)
    var w = img.getAttribute('width'), h = img.getAttribute('height');
    if (w && h) {
      img.setAttribute('src',
        'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'' +
        w + '\' height=\'' + h + '\'%3E%3C/svg%3E');
    } else {
      img.removeAttribute('src');
    }

    img.setAttribute('decoding', 'async');
    img.classList.add('lazy');
  }

  /* ------------------------------------------------------------ <picture> */

  function preparePicture(picture) {
    var sources = picture.querySelectorAll('source[srcset]');
    for (var i = 0; i < sources.length; i++) {
      var s = sources[i];
      if (s.hasAttribute('data-srcset')) continue;
      s.setAttribute('data-srcset', s.getAttribute('srcset'));
      s.removeAttribute('srcset');
    }
  }

  /* --------------------------------------------------- background inline */

  function prepareBackground(el) {
    if (alreadyHandled(el)) return;
    var style = el.getAttribute('style') || '';
    var m = style.match(/background-image\s*:\s*url\(['"]?([^'")]+)['"]?\)/i);
    if (!m) return;
    if (eagerBudget > 0) return;               // fondo del hero: se deja tal cual

    el.setAttribute('data-bg', m[1]);
    el.setAttribute('style', style.replace(m[0], ''));
    el.classList.add('lazy');
    el.dataset.lazyInit = '1';
  }

  /* ------------------------------------------------------------- barrido */

  function scan(root) {
    if (!root || root.nodeType !== 1 && root.nodeType !== 9) return;

    if (root.nodeType === 1 && root.tagName === 'IMG') {
      if (root.parentElement && root.parentElement.tagName === 'PICTURE') {
        preparePicture(root.parentElement);
      }
      prepareImg(root);
      return;
    }

    var pictures = root.querySelectorAll ? root.querySelectorAll('picture') : [];
    for (var p = 0; p < pictures.length; p++) preparePicture(pictures[p]);

    var imgs = root.querySelectorAll ? root.querySelectorAll('img') : [];
    for (var i = 0; i < imgs.length; i++) prepareImg(imgs[i]);

    if (CONFIG.handleInlineBackgrounds && root.querySelectorAll) {
      var bgs = root.querySelectorAll('[style*="background-image"]');
      for (var b = 0; b < bgs.length; b++) prepareBackground(bgs[b]);
    }
  }

  /* ------------------------------------------- observer para DOM dinámico */

  var observer = new MutationObserver(function (mutations) {
    var touched = false;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType !== 1) continue;
        scan(added[j]);
        touched = true;
      }
    }
    if (touched && window.lazyLoadInstance) window.lazyLoadInstance.update();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------- arranque */

  function init() {
    scan(document);

    if (typeof LazyLoad === 'undefined') {
      console.warn('[lazy-init] vanilla-lazyload no está cargado. ' +
                   'Debe ir ANTES de este script.');
      return;
    }

    window.lazyLoadInstance = new LazyLoad({
      elements_selector: '.lazy',
      threshold: CONFIG.threshold,
      use_native: CONFIG.useNative,
      class_loaded: 'lazy-loaded',
      class_error: 'lazy-error',
      // Si una variante del CDN falla, se cae al archivo original
      callback_error: function (el) {
        var original = el.getAttribute('data-src');
        if (original && el.tagName === 'IMG') {
          el.removeAttribute('srcset');
          el.src = stripParams(original);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify: re-escanear cuando el editor de temas recarga una sección
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
    if (window.lazyLoadInstance) window.lazyLoadInstance.update();
  });
})();